import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Phone, ArrowRight, CheckCircle2, ArrowLeft, Star } from "lucide-react";
import { SITE } from "@/lib/siteConfig";
import { SERVICE_PAGES, getServiceBySlug } from "@/lib/servicePages";
import useScrollReveal from "@/lib/useScrollReveal";
import Header from "@/components/Header";
import TopAlertBar from "@/components/TopAlertBar";
import GeoBanner from "@/components/GeoBanner";
import Marquee from "@/components/Marquee";
import Brands from "@/components/Brands";
import SeoContent from "@/components/SeoContent";
import Trust from "@/components/Trust";
import Testimonials from "@/components/Testimonials";
import Faq from "@/components/Faq";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import FloatingCallButton from "@/components/FloatingCallButton";
import WhatsAppButton from "@/components/WhatsAppButton";
import EmergencyBadge from "@/components/EmergencyBadge";

export default function ServicePage({ slug: propSlug = null }) {
  const { slug: paramSlug } = useParams();
  const slug = propSlug || paramSlug;
  const service = getServiceBySlug(slug);

  useScrollReveal();

  useEffect(() => {
    if (service) {
      document.title = `${service.title} · SOS Serrurier Express`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", service.metaDesc);
      window.scrollTo(0, 0);
    }
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white">
      <TopAlertBar />
      <Header />
      <GeoBanner />

      <main>
        <ServiceHero service={service} />
        <Marquee />
        <ServiceDetails service={service} />
        <Brands />
        <SeoContent serviceName={service.short} />
        <Trust />
        <Testimonials />
        <OtherServices currentSlug={service.slug} />
        <Faq />
        <ContactForm />
      </main>

      <Footer />
      <FloatingCallButton />
      <WhatsAppButton />
      <EmergencyBadge />
    </div>
  );
}

function ServiceHero({ service }) {
  return (
    <section data-testid="service-hero" className="relative bg-white overflow-hidden">
      <div className="bg-[#0A0A0A] text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition">
            <ArrowLeft className="w-3.5 h-3.5" />
            Accueil
          </Link>
          <span className="font-semibold tracking-wide">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2 align-middle" />
            URGENCE 24/7 · {SITE.phone}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-1.5 text-red-700 font-bold uppercase tracking-[0.15em] text-xs mb-4">
              <span className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" />
              {service.short} · Disponible maintenant
            </div>

            <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.92] text-[#0A0A0A]">
              {service.headline}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-xl leading-relaxed">
              {service.intro} Nous intervenons en moins de 30 minutes, 24h/24 et 7j/7.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={SITE.phoneHref}
                data-testid={`service-call-${service.slug}`}
                className="inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-7 py-5 font-display font-black uppercase text-lg tracking-tight transition-all hover:-translate-y-0.5 shadow-[0_6px_0_#991B1B] hover:shadow-[0_3px_0_#991B1B]"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                {service.cta}
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-[#0A0A0A] px-7 py-5 font-bold uppercase text-sm tracking-[0.15em] border-2 border-[#0A0A0A]"
              >
                Devis gratuit
              </a>
            </div>
            <div className="mt-8 flex items-center gap-3 text-sm">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />
                ))}
              </div>
              <span className="text-slate-600">
                <strong className="text-[#0A0A0A]">4,9/5</strong> sur 1 200+ avis vérifiés
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative aspect-[4/5] overflow-hidden border border-black/10">
              <img
                src={service.image}
                alt={service.headline}
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute -top-3 -right-3 bg-red-600 text-white px-4 py-2 font-display font-black uppercase text-sm tracking-tight rotate-3 shadow-lg">
                {service.price}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceDetails({ service }) {
  return (
    <section data-testid="service-details" className="bg-white py-20 sm:py-28 reveal">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-600 mb-4">
              — Ce que nous faisons
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl uppercase leading-[0.95] text-[#0A0A0A]">
              {service.short}<br />
              <span className="text-red-600">en détail.</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {service.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-slate-700 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-600 mb-4">
              — Comment ça se passe
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {service.process.map((step, i) => (
                <div key={i} className="bg-[#F3F4F6] border-l-4 border-red-600 p-6">
                  <div className="font-display font-black text-3xl text-red-600">0{i + 1}</div>
                  <div className="mt-2 font-display font-bold text-lg uppercase tracking-tight">{step.t}</div>
                  <div className="mt-1.5 text-sm text-slate-600 leading-relaxed">{step.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OtherServices({ currentSlug }) {
  const others = SERVICE_PAGES.filter((s) => s.slug !== currentSlug);
  return (
    <section data-testid="other-services" className="bg-[#F3F4F6] py-20 sm:py-28 reveal">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-600 mb-4">
          — Nos autres prestations
        </div>
        <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl uppercase leading-[0.95] text-[#0A0A0A] mb-10">
          Découvrir nos autres services.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {others.map((s) => (
            <Link
              key={s.slug}
              to={s.path}
              data-testid={`other-service-${s.slug}`}
              className="group bg-white border border-black/10 hover:border-red-600 p-6 flex flex-col justify-between transition-all hover:-translate-y-1 min-h-[180px]"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-red-600 mb-3">
                Service
              </div>
              <div>
                <div className="font-display font-black text-xl tracking-tight uppercase group-hover:text-red-600 transition">
                  {s.short}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-slate-500 group-hover:text-red-600 transition">
                  En savoir plus
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
